import { useState } from 'react';
import {
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TableSortLabel,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getTransportCustomers,
  createTransportCustomer,
  updateTransportCustomer,
  deleteTransportCustomer,
} from '../../services/transportCustomers';
import CustomerImportDialog from './CustomerImport';

type Customer = {
  id: number;
  name: string;
  businessId: string;
  customerId?: string;
  discount: number;
};

const tableHeaders: { id: keyof Customer; label: string }[] = [
  { id: 'id', label: 'ID' },
  { id: 'name', label: 'Nimi' },
  { id: 'businessId', label: 'Y-tunnus' },
  { id: 'customerId', label: 'Asiakasnumero' },
  { id: 'discount', label: 'Alennus' },
];

const Customers = () => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteCustomerId, setDeleteCustomerId] = useState<
    string | null
  >(null);
  const [editingCustomer, setEditingCustomer] =
    useState<Customer | null>(null);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    businessId: '',
    customerId: '',
    discount: '',
  });
  const [sortBy, setSortBy] = useState<keyof Customer>('id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    'asc',
  );
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const queryClient = useQueryClient();

  const { data: customers = [] } = useQuery({
    queryKey: ['transportCustomers'],
    queryFn: getTransportCustomers,
  });

  const createCustomerMutation = useMutation({
    mutationFn: createTransportCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['transportCustomers'],
      });
      handleClose();
      resetCustomerForm();
    },
    onError: (error) => {
      console.error('Failed to create customer:', error);
    },
  });

  const updateCustomerMutation = useMutation({
    mutationFn: updateTransportCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['transportCustomers'],
      });
      handleClose();
      resetCustomerForm();
    },
    onError: (error) => {
      console.error('Failed to update customer:', error);
    },
  });

  const deleteCustomerMutation = useMutation({
    mutationFn: deleteTransportCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['transportCustomers'],
      });
    },
    onError: (error) => {
      console.error('Failed to delete customer:', error);
    },
  });

  const handleSort = (column: keyof Customer) => {
    setSortBy(column);
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const sortedCustomers = [...customers]
    .filter((customer) =>
      [customer.id, customer.name, customer.businessId].some(
        (field) =>
          field
            ?.toString()
            .toLowerCase()
            .includes(search.toLowerCase()),
      ),
    )
    .sort((a, b) => {
      if (a[sortBy] < b[sortBy])
        return sortDirection === 'asc' ? -1 : 1;
      if (a[sortBy] > b[sortBy])
        return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const handleOpen = () => {
    setEditingCustomer(null);
    setOpen(true);
  };

  const handleImportDialogOpen = () => {
    setImportDialogOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleEdit = (customer: any) => {
    setEditingCustomer(customer);
    setNewCustomer(customer);
    setOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setDeleteCustomerId(id);
    setDeleteDialogOpen(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewCustomer((prev) => ({
      ...prev,
      [name]: name === 'discount' ? value.replace(/\D/, '') : value,
    }));
  };

  const handleSave = () => {
    if (!newCustomer.name.trim() || !newCustomer.businessId.trim()) {
      return;
    }

    const customerData = {
      ...newCustomer,
      discount:
        newCustomer.discount === ''
          ? 0
          : Number(newCustomer.discount),
    };

    if (editingCustomer?.id) {
      updateCustomerMutation.mutate({
        id: editingCustomer.id,
        ...customerData,
      });
    } else {
      createCustomerMutation.mutate(customerData);
    }
  };

  const resetCustomerForm = () => {
    setNewCustomer({
      name: '',
      businessId: '',
      customerId: '',
      discount: '',
    });
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <TextField
          label="Suodata asiakkaita"
          variant="outlined"
          size="small"
          sx={{ flexGrow: 1, mr: 2 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpen}
        >
          Luo uusi asiakas
        </Button>
        <Button
          variant="outlined"
          color="primary"
          sx={{ ml: 2 }}
          onClick={handleImportDialogOpen}
        >
          Tuo Mirasta
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: '1px solid #e0e0e0' }}
      >
        <Table size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f2f2f2' }}>
              {tableHeaders.map((header) => (
                <TableCell key={header.id}>
                  <TableSortLabel
                    active={sortBy === header.id}
                    direction={
                      sortBy === header.id ? sortDirection : 'asc'
                    }
                    onClick={() => handleSort(header.id)}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell>Toiminnot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedCustomers.map((customer) => (
              <TableRow key={customer.id}>
                <TableCell>{customer.id}</TableCell>
                <TableCell>{customer.name}</TableCell>
                <TableCell>{customer.businessId}</TableCell>
                <TableCell>{customer.customerId}</TableCell>
                <TableCell>{customer.discount}%</TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => handleEdit(customer)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteClick(customer.id)}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Vahvista poisto</DialogTitle>
        <DialogContent>
          Oletko varma, että haluat poistaa tämän asiakkaan?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            color="primary"
          >
            Peruuta
          </Button>
          <Button
            onClick={() => {
              if (deleteCustomerId) {
                deleteCustomerMutation.mutate(deleteCustomerId);
              }
              setDeleteDialogOpen(false);
            }}
            color="error"
            variant="contained"
          >
            Poista
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingCustomer ? 'Muokkaa asiakasta' : 'Luo uusi asiakas'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Nimi"
            name="name"
            fullWidth
            margin="dense"
            value={newCustomer.name}
            onChange={handleChange}
          />
          <TextField
            label="Y-tunnus"
            name="businessId"
            fullWidth
            margin="dense"
            value={newCustomer.businessId}
            onChange={handleChange}
          />
          <TextField
            label="Asiakasnumero"
            name="customerId"
            fullWidth
            margin="dense"
            value={newCustomer.customerId}
            onChange={handleChange}
          />
          <TextField
            label="Alennus"
            name="discount"
            type="number"
            fullWidth
            margin="dense"
            value={newCustomer.discount}
            onChange={handleChange}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="primary"
            variant="outlined"
          >
            Sulje
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={
              !newCustomer.name.trim() ||
              !newCustomer.businessId.trim() ||
              createCustomerMutation.isPending ||
              updateCustomerMutation.isPending
            }
          >
            {createCustomerMutation.isPending ||
            updateCustomerMutation.isPending
              ? 'Tallennetaan...'
              : 'Tallenna'}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomerImportDialog
        open={importDialogOpen}
        customers={customers}
        onClose={() => setImportDialogOpen(false)}
      />
    </>
  );
};

export default Customers;
